import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useOtpStore } from "../store";
import {
  confirmPhoneReq,
  confirmResetPasswordCodeReq,
  registerReq,
  resetPasswordReq,
} from "@/api/services/auth";
import { paths } from "@/navigation/paths";

export const useVerifyOtp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [resendDisabled, setResendDisabled] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { code, setCode, setLoading, setError, isLoading, errorMessage } =
    useOtpStore();

  const phone = location.state?.phone || "";
  const isSignUp = location.state?.isSignUp || false;
  const isForgotPassword = location.state?.isForgotPassword || false;

  // Clear error when component mounts
  useEffect(() => {
    setError(null);
  }, [setError]);

  useEffect(() => {
    if (!phone) {
      navigate(paths.LOGIN);
    }
  }, [phone, navigate]);

  const onCodeChange = (value: string) => {
    setCode(value);
    setError(null);
  };

  const handleResendCode = async () => {
    try {
      setLoading(true);
      const data = { phone };
      const response = isForgotPassword
        ? await resetPasswordReq(data)
        : await registerReq(data);

      if (response.data === "success" || response.data === true) {
        setResendDisabled(true);
        setSuccessMessage("Code has been resent");

        setTimeout(() => {
          setResendDisabled(false);
          setSuccessMessage(null);
        }, 2 * 60 * 1000); // 2 minutes

        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to resend code. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async () => {
    if (code.length !== 4) {
      setError("Please enter a valid 4-digit code");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isForgotPassword) {
        // For forgot password flow
        const response = await confirmResetPasswordCodeReq({ phone, password: code });
        if (response.data === true) {
          navigate(paths.RESET_PASSWORD, {
            state: { phone, code },
          });
          // Clear the OTP form after successful navigation
          useOtpStore.getState().reset();
        } else {
          setError("Invalid verification code. Please try again.");
        }
      } else {
        // For sign up flow
        const response = await confirmPhoneReq({ phone, password: code });
        // Check if response is successful
        if (response.status === 200 || response.status === 201) {
          if (response.data?.id) {
            navigate(paths.USER_INFO, {
              state: {
                phone,
                id: response.data.id,
              },
            });
            // Clear the OTP form after successful navigation
            useOtpStore.getState().reset();
          } else {
            setError("Invalid response from server. Please try again.");
          }
        } else {
          setError("Verification failed. Please check the code and try again.");
        }
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Verification failed. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return {
    code,
    phone,
    isLoading,
    errorMessage,
    successMessage,
    resendDisabled,
    onCodeChange,
    onSubmit,
    handleResendCode,
  };
};
